
import $ from "jquery"
import Promise from "plugins/bluebird"
import isBlankStr from "underscore.string/isBlank"

import DomIdGenerator from "app/utils/dom_id_generator"

import GoogleMaps_asyncLoad from "app/google-maps/index"

import SwitchMode from "components/widgets/switch_mode"
import DataRetriever from "app/data_retriever"

import Notifier from "app/notifier"
import {
  captureException,
} from "app/error_reporting"

import I18n from "plugins/i18n-js"


const RejectionReasons = {
  FAILURE_CASE_HANDLED: "FAILURE_CASE_HANDLED",
}


let _retained_panoramas_per_dom_id = {} as {
  [key: string]: google.maps.StreetViewPanorama
}


interface Selector {
  main:         string | JQuery
  switch:       string
  street_view:  string
}

interface InputSelector {
  main?:         string | JQuery
  switch?:       string
  street_view?:  string
}

const default_selector = {
  main:         ".cell--overseas_properties-shared--project_entry_pictures_and_map_browser--with-toggle",
  switch:       ".cell--overseas_properties-shared--project_entry_pictures_and_map_browser--with-toggle__switch--street-view",
  street_view:  ".cell--overseas_properties-shared--project_entry_pictures_and_map_browser--with-toggle__content-for-mode-street .js-google-street-view",
}


const init = (selector?: InputSelector) => {
  const sele: Selector = Object.assign({}, default_selector, selector)
  if (DataRetriever.get("current-location-country-code-is-china")) { return }

  const $el = $(sele.main)

  $el.each((_idx, el_in_each) => {
    const $el_in_each = $(el_in_each)
    const $switch = $el_in_each.find(sele.switch)
    const $street_view_box = $el_in_each.find(sele.street_view)
    const street_view_box = $street_view_box[0]

    let dom_id = street_view_box.id
    if (isBlankStr(street_view_box.id)) {
      dom_id = DomIdGenerator.generateNewId()
      street_view_box.setAttribute("id", dom_id)
    }

    $switch
    .on("click", () => {
      // The mode switching won't be done immediately
      // So we subscribe to the event instead
      //
      // Don't use `setTimeout`
      SwitchMode.once("switch_mode:mode_switched", () => {
        // Only create street view object when visible
        if (! $street_view_box.is(":visible")) { return }

        const existing_panorama = _retained_panoramas_per_dom_id[dom_id]

        const panorama_promise: Promise<google.maps.StreetViewPanorama> = Promise.resolve()
        .then(() => {
          if (existing_panorama != null) {
            return existing_panorama
          }

          return GoogleMaps_asyncLoad()
          .then((google_maps) => {
            // # `getAttribute` is used instead of `dataset` for better compatibility
            // # Both return string
            const lat_str = street_view_box.getAttribute("data-lat")
            const lng_str = street_view_box.getAttribute("data-lng")
            if (lat_str == null || lng_str == null) {
              Notifier.showError(I18n.t("js.app.street_view.errors.street_view_unavailable_at_this_location"))
              throw RejectionReasons.FAILURE_CASE_HANDLED
            }

            const lat = parseFloat(lat_str)
            const lng = parseFloat(lng_str)
            const latlng_obj = new google_maps.LatLng(lat, lng)

            const sv = new google_maps.StreetViewService()

            return new Promise((resolve, reject) => {
              // Default is 50m
              sv.getPanorama({location: latlng_obj, radius: 50}, (data, status) => {
                const location_pano = (data == null || data.location == null) ? null : data.location.pano
                if (location_pano == null) {
                  Notifier.showError(I18n.t("js.app.street_view.errors.street_view_unavailable_at_this_location"))
                  reject(RejectionReasons.FAILURE_CASE_HANDLED)
                  return
                }

                if (status === google_maps.StreetViewStatus.OK) {
                  const new_panorama = new google_maps.StreetViewPanorama(
                    street_view_box,
                  )
                  new_panorama.setPano(location_pano)
                  new_panorama.setPov({
                    heading:  0,
                    pitch:    0,
                  })

                  _retained_panoramas_per_dom_id[dom_id] = new_panorama

                  // return new_panorama
                  return new_panorama
                }
                else {
                  Notifier.showError(I18n.t("js.app.street_view.errors.street_view_unavailable_at_this_location"))
                  reject(RejectionReasons.FAILURE_CASE_HANDLED)
                }
              })
            })
          })
        })

        panorama_promise.then((panorama) => {
          panorama.setVisible(true)
        })
        .catch((reason_or_error) => {
          if (reason_or_error === RejectionReasons.FAILURE_CASE_HANDLED) {
            return
          }

          captureException(reason_or_error)
        })
        .done()
      })
    })
  })
}

// # Call this function to avoid memory leak
// # panorama objects are kept on initialization on purpose
// # We need to remove those objects manually
// # You have to call this function, otherwise there will be memory leak.
const removeAllPanoramas = () => {
  _retained_panoramas_per_dom_id = {}
}

const  _setAllPanoramaVisible = () => {
  Object.values(_retained_panoramas_per_dom_id).forEach((panorama) => {
    panorama.setVisible(true)
  })
}

const onLoad = () => {
  SwitchMode.on("switch_mode:mode_switched", () => {
    _setAllPanoramaVisible()
  })
}

export default {
  init,
  removeAllPanoramas,
  onLoad,
}
